import { useCallback, useEffect, useRef, useState } from 'react';
import { VadRunner, createBrowserSileroBackend } from '../audio/vadRunner';

export interface UseVadSessionOptions {
  onSegment: (wavBase64: string, durationSec: number) => void;
}

export interface UseVadSession {
  ready: boolean;
  recording: boolean;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  pause: () => void;
  resume: () => void;
}

export function useVadSession(opts: UseVadSessionOptions): UseVadSession {
  const runnerRef = useRef<VadRunner | null>(null);
  const speechStartTsRef = useRef<number>(0);
  // Latest onSegment callback held in a ref so init() can use an empty
  // dependency array and doesn't need to tear down the VadRunner every time
  // the parent re-renders with a fresh inline onSegment.
  const onSegmentRef = useRef(opts.onSegment);
  useEffect(() => {
    onSegmentRef.current = opts.onSegment;
  }, [opts.onSegment]);
  const [ready, setReady] = useState(false);
  const [recording, setRecording] = useState(false);

  const init = useCallback(async () => {
    if (runnerRef.current) return;
    const backend = await createBrowserSileroBackend();
    const runner = new VadRunner({
      backend,
      sampleRate: 16000,
      onSpeechStart: () => {
        speechStartTsRef.current = performance.now();
      },
      onSegment: (b64) => {
        const elapsed = (performance.now() - speechStartTsRef.current) / 1000;
        onSegmentRef.current(b64, elapsed);
      },
    });
    await runner.init();
    runnerRef.current = runner;
    setReady(true);
  }, []);

  const start = useCallback(async () => {
    await init();
    runnerRef.current?.start();
    setRecording(true);
  }, [init]);

  const stop = useCallback(async () => {
    await runnerRef.current?.stop();
    runnerRef.current = null;
    setReady(false);
    setRecording(false);
  }, []);

  const pause = useCallback(() => runnerRef.current?.pause(), []);
  const resume = useCallback(() => runnerRef.current?.start(), []);

  useEffect(() => {
    return () => {
      runnerRef.current?.stop().catch(() => undefined);
      runnerRef.current = null;
    };
  }, []);

  return { ready, recording, start, stop, pause, resume };
}
